import { serviceRecordListQuerySchema, type Page, type RecordLastDoneInput, type ServiceRecordListQuery } from '@chukua/common';
import { useCallback, useEffect, useState } from 'react';
import { useListQuery } from '../../../../../shared/hooks/use-list-query';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../../../shared/policies/api-error-message';
import { toListParams } from '../../../../../shared/policies/list-params';
import { ApiError } from '../../../../../shared/shared.api';
import { listServiceRecords, listTruckServiceItems, recordLastDone, type ServiceRecordView, type TruckServiceItemView } from '../truck-service.api';

export type ServiceRecordsState =
  { status: 'loading' } | { status: 'loaded'; page: Page<ServiceRecordView>; queryKey: string } | { status: 'forbidden' } | { status: 'error' };

const keyOf = (query: ServiceRecordListQuery) => toListParams(query).toString();

/** The truck's plan is bounded by the item catalogue, so one page of the largest size is every type of service it can record. */
const ALL_PLAN_ITEMS = { page: 1, pageSize: 100, q: undefined, sort: 'name', order: 'asc', source: undefined } as const;

async function loadRecords(truckId: string, query: ServiceRecordListQuery): Promise<ServiceRecordsState> {
  try {
    return { status: 'loaded', page: await listServiceRecords(truckId, query), queryKey: keyOf(query) };
  } catch (err) {
    return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
  }
}

/**
 * A truck's service history, a page at a time from the server, and the types of service it can record (the items of its plan).
 * A form failure stays inline; a recorded entry toasts.
 */
export function useServiceRecords(truckId: string) {
  const list = useListQuery(serviceRecordListQuerySchema);
  const { query } = list;
  const [state, setState] = useState<ServiceRecordsState>({ status: 'loading' });
  const [serviceTypes, setServiceTypes] = useState<TruckServiceItemView[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const fetchRecords = useCallback(() => loadRecords(truckId, query), [truckId, query]);

  useEffect(() => {
    let cancelled = false;
    fetchRecords().then((next) => {
      if (!cancelled) setState(next);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchRecords]);

  useEffect(() => {
    let cancelled = false;
    listTruckServiceItems(truckId, ALL_PLAN_ITEMS)
      .then((page) => !cancelled && setServiceTypes(page.items))
      .catch(() => !cancelled && setServiceTypes([]));
    return () => {
      cancelled = true;
    };
  }, [truckId]);

  async function addEntry(item: { id: string; name: string }, input: RecordLastDoneInput): Promise<boolean> {
    setSaveError(null);
    setSaving(true);
    try {
      await recordLastDone(truckId, item.id, input);
      setState(await fetchRecords());
      toast.success(`${item.name} service recorded`);
      return true;
    } catch (err) {
      setSaveError(apiErrorMessage(err) ?? 'Could not save — check the fields and try again.');
      return false;
    } finally {
      setSaving(false);
    }
  }

  return {
    state,
    list,
    serviceTypes,
    isRefreshing: state.status === 'loaded' && state.queryKey !== keyOf(query),
    saveError,
    saving,
    resetSaveError: () => setSaveError(null),
    addEntry,
  };
}
